import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Check, Upload, Loader, Footprints, Mic2, Users, Sparkles, Brain, MapPin, HandHeart, FileText, X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import FirstImpressionCard from "../FirstImpressionCard";

/**
 * Unified dashboard hero — merges the old "One Resume / Endless Opportunities"
 * strip and the "Resmo is with you at every step" Network promo into a
 * single, denser card. Keeps the resume-upload + welcome greeting at the top,
 * then surfaces the companion-through-every-step positioning + Network entry
 * pills underneath.
 *
 * Renders for both authed and anonymous users; CTAs swap accordingly.
 */
export default function HeroStrip({
  user, firstName, hasResume, isPro, pickMode, modeSaving, refresh,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setPasteSaved(true);
      setPasteOpen(true);
    } finally {
      setUploading(false);
    }
  };

  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteSaving, setPasteSaving] = useState(false);
  const [pasteError, setPasteError] = useState("");
  const [pasteSaved, setPasteSaved] = useState(false);

  const closePaste = () => {
    setPasteOpen(false);
    setPasteSaved(false);
    setPasteError("");
  };

  const savePasted = async () => {
    if (pasteText.trim().length < 30) {
      setPasteError("That looks too short to be a resume — paste the full text.");
      return;
    }
    setPasteSaving(true);
    setPasteError("");
    try {
      await api.post("/resume/text", { content: pasteText });
      await refresh();
      setPasteText("");
      setPasteSaved(true);
    } catch (err) {
      setPasteError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPasteSaving(false);
    }
  };

  return (
    <div className="relative mb-6 md:mb-8" data-testid="hero-strip">
      {/* Mascot peek (desktop only) */}
      <div
        className="hidden md:block absolute pointer-events-none select-none"
        style={{ left: -28, top: 18, zIndex: 2 }}
        aria-hidden="true"
        data-testid="hero-mascot"
      >
        <img
          src="/brand/resmo-welcoming.png"
          alt=""
          className="rm-shadow-bob"
          style={{ width: 200, height: 200, objectFit: "contain" }}
          draggable={false}
        />
      </div>

      <div
        className="rm-card-static p-4 md:p-6 md:pl-52 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0F1B2D 0%, #1E3A5F 60%, #0F1B2D 100%)",
          borderColor: "#0F1B2D",
          boxShadow: "5px 5px 0 0 #FFC145",
          color: "white",
        }}
      >
        {/* Mobile mascot fallback */}
        <div className="md:hidden flex items-center gap-3 mb-3">
          <div className="flex-shrink-0 flex items-center justify-center"
               style={{ width: 44, height: 44, background: "#FFF8E7",
                        border: "2px solid #0F1B2D", boxShadow: "2px 2px 0 0 #FF6A00" }}>
            <img src="/brand/resmo-character.png" alt="" style={{ width: 36, height: 36, objectFit: "contain" }} draggable={false} />
          </div>
          <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#FFC145" }}>
            Your career intelligence companion
          </div>
        </div>

        {/* Top row: greeting + resume status/CTA */}
        <div className="flex items-start gap-3 md:gap-5 flex-wrap mb-3">
          <div className="flex-1 min-w-[200px]">
            <div className="hidden md:block font-mono-rm uppercase text-[10px] tracking-widest mb-1.5" style={{ color: "#FFC145" }}>
              · Your career companion
            </div>
            <h1
              className="font-display leading-none mb-1.5"
              style={{
                fontSize: "clamp(1.5rem, 3.2vw, 2.15rem)",
                color: "white",
                letterSpacing: "-0.02em",
              }}
            >
              Resmo is with you at <span style={{ color: "#FFC145" }}>every step</span>.
            </h1>
            <div
              className="font-display uppercase tracking-tight leading-tight mb-2"
              style={{
                fontSize: "clamp(0.8rem, 1.3vw, 1.05rem)",
                letterSpacing: "-0.005em",
              }}
              data-testid="hero-subtagline"
            >
              <span style={{ color: "#FF8636" }}>Be Discovered.</span>{" "}
              <span style={{ color: "#20C1A7" }}>Be Understood.</span>{" "}
              <span style={{ color: "#FFC145" }}>Beyond the Resume.</span>
            </div>
            <div
              className="text-sm leading-snug"
              style={{ color: "#E2E8F0", fontWeight: 500 }}
              data-testid="hero-greeting"
            >
              {user && firstName
                ? <>Welcome back, <span style={{ color: "white", fontWeight: 700 }}>{firstName}</span>. Polished resume, network of stones, knock-out interview — Resmo holds your hand the whole way.</>
                : <>Polished resume, network of stones, knock-out interview — Resmo holds your hand the whole way.</>}
            </div>
          </div>

          {user && (
            <div className="flex items-center gap-2">
              {hasResume ? (
                <>
                  <span className="font-mono-rm text-[10px] uppercase tracking-widest flex items-center gap-1"
                        style={{ color: "#10B981" }}>
                    <Check size={12} /> Resume on file
                  </span>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="rm-btn text-[10px] px-2.5 py-1"
                    style={{ background: "white", color: "#0F1B2D",
                             border: "2px solid white", boxShadow: "2px 2px 0 0 #FFC145" }}
                    data-testid="hero-replace-resume"
                  >
                    {uploading ? <Loader size={10} className="animate-spin" /> : <><Upload size={10} /> Replace</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPasteOpen(true)}
                    className="rm-btn text-[10px] px-2.5 py-1"
                    style={{ background: "transparent", color: "white",
                             border: "2px solid rgba(255,255,255,0.5)" }}
                    title="Paste your resume as plain text"
                    data-testid="hero-paste-resume"
                  >
                    <FileText size={10} /> Paste
                  </button>
                </>
              ) : (
                <>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="rm-btn rm-btn-primary text-[11px] px-3 py-1.5"
                  data-testid="hero-add-resume"
                >
                  {uploading ? <><Loader size={11} className="animate-spin" /> Uploading…</> : <><Upload size={11} /> Add resume</>}
                </button>
                <button
                  type="button"
                  onClick={() => setPasteOpen(true)}
                  className="rm-btn text-[11px] px-3 py-1.5"
                  style={{ background: "transparent", color: "white",
                           border: "2px solid rgba(255,255,255,0.5)" }}
                  data-testid="hero-paste-resume"
                >
                  <FileText size={11} /> Paste it
                </button>
                </>
              )}
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden"
                     onChange={(e) => upload(e.target.files?.[0])} />
              {pasteOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center px-4"
                     style={{ background: "rgba(15,27,45,0.75)" }}
                     data-testid="hero-paste-modal">
                  <div className="rm-card-static p-5 md:p-6 w-full max-w-xl" style={{ background: "white" }}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="rm-label" style={{ color: "#FF6A00" }}>
                          {pasteSaved ? "Resume saved" : "Paste your resume"}
                        </div>
                        <div className="font-display text-lg uppercase" style={{ color: "#0F1B2D" }}>
                          {pasteSaved ? "Got it — here's my first read" : "Copy-paste the full text"}
                        </div>
                      </div>
                      <button type="button" onClick={closePaste}
                              className="p-1 hover:opacity-70" data-testid="hero-paste-close">
                        <X size={17} style={{ color: "#64748B" }} />
                      </button>
                    </div>
                    {pasteSaved ? (
                      <>
                        <FirstImpressionCard />
                        <div className="flex justify-end mt-4">
                          <button type="button" onClick={closePaste}
                                  className="rm-btn rm-btn-primary text-xs px-4 py-2"
                                  data-testid="hero-paste-done">
                            Let's go →
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <textarea
                          className="rm-textarea w-full"
                          style={{ minHeight: 240 }}
                          placeholder="Paste your entire resume here — headings, bullets, everything."
                          value={pasteText}
                          onChange={(e) => setPasteText(e.target.value)}
                          autoFocus
                          data-testid="hero-paste-textarea"
                        />
                        {pasteError && (
                          <div className="mt-2 text-xs font-bold" style={{ color: "#B91C1C" }} data-testid="hero-paste-error">
                            {pasteError}
                          </div>
                        )}
                        <div className="flex justify-end gap-2 mt-4">
                          <button type="button" onClick={closePaste}
                                  className="rm-btn rm-btn-ghost text-xs px-3 py-2" data-testid="hero-paste-cancel">
                            Cancel
                          </button>
                          <button type="button" onClick={savePasted}
                                  disabled={pasteSaving || pasteText.trim().length < 30}
                                  className="rm-btn rm-btn-primary text-xs px-4 py-2" data-testid="hero-paste-save">
                            {pasteSaving ? <><Loader size={12} className="animate-spin" /> Saving…</> : "Save resume"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {!user && (
            <div className="flex items-center gap-2">
              <Link to="/register" className="rm-btn rm-btn-primary text-[11px] px-3 py-1.5">
                <ArrowRight size={11} /> Create account
              </Link>
              <Link to="/login" className="rm-btn rm-btn-ghost text-[11px] px-3 py-1.5"
                    style={{ color: "#FFC145" }}>
                Sign in
              </Link>
            </div>
          )}
        </div>

        {/* Companion line — no Network pills, no Open Network CTA; both
            moved into the dedicated Network surface + Career Tools grid. */}
        <div className="font-mono-rm text-[11px] uppercase tracking-widest pt-3 mt-1"
             style={{ color: "#FFC145", borderTop: "1px dashed rgba(255,193,69,0.35)" }}>
          · Resume → Research → Network → Interview → Reflection · I'm your coach AND your cheerleader.
        </div>
      </div>
    </div>
  );
}
